import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Server, Trash2 } from "lucide-react";

import { api, ApiError, type Connector, type NvrInput } from "../../api";
import { InfoHint } from "../../components/InfoHint";
import { Modal } from "../../components/Modal";
import { SearchableSelect } from "../../components/SearchableSelect";
import { useScope } from "../../components/ScopeContext";
import {
  BRANDS_WITH_SUB_STREAM,
  BRAND_OPTIONS,
  EmptyState,
  Note,
  SettingsSection,
} from "./shared";

export const emptyNvr: NvrInput = {
  id: "",
  name: "",
  brand: "hikvision",
  host: "",
  port: 554,
  username: "",
  password: "",
  rtsp_url_template: null,
  store: "",
  connector_id: null,
};

export type NvrDraft = NvrInput & { created_at?: string };

export function NvrsSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { scope, stores } = useScope();
  const list = useQuery({ queryKey: ["nvrs"], queryFn: api.nvrs.list });
  const connectors = useQuery({ queryKey: ["connectors"], queryFn: api.connectors.list });
  const [draft, setDraft] = useState<NvrDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upsert = useNvrUpsert({
    onSuccess: () => {
      setDraft(null);
      setError(null);
    },
    onError: setError,
  });
  const connectorName = (id?: string | null) => {
    const c = (connectors.data ?? []).find((x) => x.id === id);
    return c ? c.name || c.id : id ?? "";
  };
  const del = useMutation({
    mutationFn: api.nvrs.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nvrs"] }),
  });

  const storeNames = stores.map((s) => s.name);
  const defaultStore = scope.kind === "store" ? scope.store : "";
  const visible = useMemo(() => {
    const all = list.data ?? [];
    return defaultStore ? all.filter((n) => (n.store ?? "") === defaultStore) : all;
  }, [list.data, defaultStore]);

  return (
    <SettingsSection
      title="Recorders (NVRs)"
      description="Save a recorder once, then add its cameras by channel number."
      hint={
        <>
          Storing the address and credentials here means each camera only needs
          a channel number — the stream URLs are built from the recorder's
          brand. Editing a recorder rewrites the URLs of every camera on it and
          applies them to live capture immediately. A recorder on a site this
          system cannot reach directly is reached through that store's
          connector, using its address on the store network.
        </>
      }
      actions={
        canEdit && (
          <button
            className="bf-button disabled:opacity-40"
            onClick={() => setDraft({ ...emptyNvr, store: defaultStore || storeNames[0] || "" })}
            disabled={storeNames.length === 0}
            title={storeNames.length === 0 ? "Create a store first" : undefined}
          >
            <Plus size={16} className="mr-2" /> Add recorder
          </button>
        )
      }
    >
      {error && <Note tone="error">{error}</Note>}
      {canEdit && storeNames.length === 0 && (
        <Note tone="warning">Add a store first — every recorder belongs to one.</Note>
      )}

      {visible.length === 0 ? (
        <EmptyState
          icon={Server}
          title={
            defaultStore ? `No recorders in “${defaultStore}”` : "No recorders yet"
          }
          hint="Recorders are optional: cameras can also be added by their own stream URL."
        />
      ) : (
        <div className="bf-card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Brand</th>
                <th className="p-3">Host</th>
                <th className="p-3">Store</th>
                <th className="p-3">Streams</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((n) => (
                <tr key={n.id} className="border-t border-white/5">
                  <td className="p-3">
                    <div className="font-medium">{n.name || n.id}</div>
                    <div className="text-xs text-bf-muted">ID: {n.id}</div>
                  </td>
                  <td className="p-3 capitalize">{n.brand}</td>
                  <td className="p-3 font-mono text-xs">
                    {n.host}:{n.port}
                    {n.connector_id && (
                      <div className="font-sans text-bf-muted">
                        via {connectorName(n.connector_id)}
                      </div>
                    )}
                    <div className="text-bf-muted">
                      {n.username || "no user"} ·{" "}
                      {n.password_set ? "password stored" : "no password"}
                    </div>
                  </td>
                  <td className="p-3">
                    {n.store ? (
                      <span className="rounded-full bg-bf-violet/20 px-2 py-0.5 text-xs text-bf-text">
                        {n.store}
                      </span>
                    ) : (
                      <span className="text-xs text-bf-muted">—</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-bf-muted">
                    {(n.supports_sub_stream ?? BRANDS_WITH_SUB_STREAM.has(n.brand))
                      ? "Main + sub"
                      : "Main only"}
                  </td>
                  <td className="p-3 text-right">
                    {canEdit && (
                      <div className="flex justify-end gap-3">
                        <button
                          className="text-bf-muted hover:text-bf-text"
                          aria-label={`Edit ${n.id}`}
                          onClick={() =>
                            setDraft({
                              id: n.id,
                              name: n.name,
                              brand: n.brand,
                              host: n.host,
                              port: n.port,
                              username: n.username,
                              password: null,
                              rtsp_url_template: n.rtsp_url_template,
                              store: n.store ?? "",
                              connector_id: n.connector_id ?? null,
                              created_at: n.created_at,
                            })
                          }
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          className="text-bf-muted hover:text-red-400"
                          aria-label={`Delete ${n.id}`}
                          onClick={() => del.mutate(n.id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {draft && (
        <NvrDraftModal
          draft={draft}
          setDraft={setDraft}
          onSubmit={(n) => upsert.mutate(n)}
          pending={upsert.isPending}
        />
      )}
    </SettingsSection>
  );
}

export function useNvrUpsert({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (message: string) => void;
}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (n: NvrDraft) => (n.created_at ? api.nvrs.update(n) : api.nvrs.upsert(n)),
    onSuccess: () => {
      onSuccess();
      qc.invalidateQueries({ queryKey: ["nvrs"] });
      qc.invalidateQueries({ queryKey: ["stores"] });
      qc.invalidateQueries({ queryKey: ["connectors"] });
      // Editing a recorder rewrites its cameras' URLs server-side.
      qc.invalidateQueries({ queryKey: ["cameras"] });
    },
    onError: (e: unknown) =>
      onError(e instanceof ApiError ? e.message : "Could not save the recorder."),
  });
}

export function NvrDraftModal({
  draft,
  setDraft,
  onSubmit,
  pending,
}: {
  draft: NvrDraft;
  setDraft: (n: NvrDraft | null) => void;
  onSubmit: (n: NvrDraft) => void;
  pending: boolean;
}) {
  const { stores } = useScope();
  const connectors = useQuery({ queryKey: ["connectors"], queryFn: api.connectors.list });
  const storeConnectors: Connector[] = (connectors.data ?? []).filter(
    (c) => c.store === draft.store,
  );
  const [showAdvancedUrl, setShowAdvancedUrl] = useState(
    Boolean(draft.rtsp_url_template && draft.brand === "generic"),
  );

  return (
    <Modal
      onClose={() => setDraft(null)}
      labelledBy="nvr-draft-title"
      panelClassName="w-full max-w-lg"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(draft);
        }}
        className="space-y-3"
      >
        <h3 id="nvr-draft-title" className="text-lg font-medium text-white">
          {draft.created_at ? "Edit recorder" : "Add a recorder"}
        </h3>

        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="mb-1 block text-xs text-bf-muted">ID</span>
            <input
              className="bf-input font-mono text-xs"
              value={draft.id}
              onChange={(e) => setDraft({ ...draft, id: e.target.value })}
              disabled={!!draft.created_at}
              required
              placeholder="nvr-front"
            />
          </label>
          <label>
            <span className="mb-1 block text-xs text-bf-muted">Display name</span>
            <input
              className="bf-input"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Front shop"
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="mb-1 block text-xs text-bf-muted">Brand</span>
            <SearchableSelect
              ariaLabel="Brand"
              value={draft.brand}
              onChange={(v) => setDraft({ ...draft, brand: v })}
              searchThreshold={99}
              options={BRAND_OPTIONS.map((b) => ({
                value: b.id,
                label: b.label,
                hint: BRANDS_WITH_SUB_STREAM.has(b.id)
                  ? "Main + sub stream"
                  : "Main stream only",
              }))}
            />
          </div>
          <div>
            <span className="mb-1 block text-xs text-bf-muted">Store</span>
            <SearchableSelect
              ariaLabel="Store"
              value={draft.store ?? ""}
              onChange={(v) => {
                const keep = (connectors.data ?? []).some(
                  (c) => c.id === draft.connector_id && c.store === v,
                );
                setDraft({ ...draft, store: v, connector_id: keep ? draft.connector_id : null });
              }}
              placeholder="Choose a store…"
              options={stores.map((s) => ({
                value: s.name,
                label: s.display_name || s.name,
                hint: s.location || undefined,
              }))}
            />
          </div>
        </div>

        <div>
          <span className="mb-1 block text-xs text-bf-muted">
            Reach via
            <InfoHint className="ml-1">
              A connector at the store forwards this recorder to the system over
              an outbound-only link, so the site needs no port forwarding.
            </InfoHint>
          </span>
          <SearchableSelect
            ariaLabel="Reach via"
            value={draft.connector_id ?? ""}
            onChange={(v) => setDraft({ ...draft, connector_id: v || null })}
            searchThreshold={99}
            options={[
              { value: "", label: "Direct", hint: "This system connects to the address below" },
              ...storeConnectors.map((c) => ({
                value: c.id,
                label: c.name || c.id,
                hint: c.online ? "Connector, online" : "Connector, offline",
              })),
            ]}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className="col-span-2">
            <span className="mb-1 block text-xs text-bf-muted">
              {draft.connector_id ? "Address on the store network" : "Host (IP or DNS)"}
            </span>
            <input
              className="bf-input font-mono text-xs"
              value={draft.host}
              onChange={(e) => setDraft({ ...draft, host: e.target.value })}
              placeholder="192.168.1.50"
              required
            />
          </label>
          <label>
            <span className="mb-1 block text-xs text-bf-muted">Port</span>
            <input
              className="bf-input"
              type="number"
              min={1}
              max={65535}
              value={draft.port}
              onChange={(e) => setDraft({ ...draft, port: Number(e.target.value) })}
              required
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="mb-1 block text-xs text-bf-muted">Username</span>
            <input
              className="bf-input"
              value={draft.username}
              onChange={(e) => setDraft({ ...draft, username: e.target.value })}
              autoComplete="off"
            />
          </label>
          <label>
            <span className="mb-1 block text-xs text-bf-muted">
              Password
              {draft.created_at && (
                <span className="ml-1 text-xs text-bf-muted">
                  (blank keeps the current one)
                </span>
              )}
            </span>
            <input
              className="bf-input"
              type="password"
              value={draft.password ?? ""}
              onChange={(e) => setDraft({ ...draft, password: e.target.value })}
              autoComplete="new-password"
              placeholder={draft.created_at ? "••••••••" : ""}
            />
          </label>
        </div>

        <button
          type="button"
          onClick={() => setShowAdvancedUrl((s) => !s)}
          className="text-xs text-bf-muted hover:text-bf-text"
        >
          {showAdvancedUrl ? "Hide URL template" : "Custom URL template (advanced)"}
        </button>

        {showAdvancedUrl && (
          <label>
            <span className="mb-1 block text-xs text-bf-muted">
              Stream URL template
            </span>
            <input
              className="bf-input font-mono text-xs"
              value={draft.rtsp_url_template ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, rtsp_url_template: e.target.value || null })
              }
              placeholder="rtsp://{user}:{pass}@{host}:{port}/Streaming/Channels/{channel}01"
            />
            <span className="text-xs text-bf-muted">
              Placeholders: <code>{"{user}"}</code> <code>{"{pass}"}</code>{" "}
              <code>{"{host}"}</code> <code>{"{port}"}</code>{" "}
              <code>{"{channel}"}</code>. Blank uses the brand default.
              <InfoHint className="ml-1">
                A custom template disables automatic sub-stream URLs: only the
                brand defaults have a known second stream, and guessing one
                would produce a URL that fails at connect time.
              </InfoHint>
            </span>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="bf-button bg-white/10 bg-none"
            onClick={() => setDraft(null)}
          >
            Cancel
          </button>
          <button className="bf-button disabled:opacity-40" type="submit" disabled={pending}>
            {pending ? "Saving…" : "Save recorder"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
